import { ModelView } from "../model.view";

import {
  DefaultReferenceDataStoreModel,
  ReferenceDataModelVersion,
  ReferenceDataStoreModel,
} from "../../store/reference-data.store.model";
import { GlossDateAsObjectType } from "../../store/types/store.types";

export type ReferenceDate = string;
export type ReferenceBase = string;
export type ReferenceCross = string;
export type ReferenceIdParts = [ReferenceDate, ReferenceBase, ReferenceCross];
export type ReferenceId = `${ReferenceDate}|${ReferenceBase}|${ReferenceCross}`;
export const enum ReferenceDataSource {
  FOREX = "GLOSS-FX",
  MANUAL = "manual",
  MOCK = "mock",
  LEGACY = "legacy",
}

/**
 * Interface representing the reference data.
 *
 * e.g. USDHKD = 7.8, USD is base, HKD is symbol, 7.8 is the exchange rate
 * e.g. AAPLUSD = 232, AAPL is base, USD is symbol, 232 is the exchange rate
 */
export interface IReferenceData {
  date: GlossDateAsObjectType;
  base: string;
  rate: number;
  symbol: string;
  source: ReferenceDataSource | string;
}

export const isMoreRecent = (a: ReferenceDataView, b: ReferenceDataView): boolean => {
  if (a.date.date !== b.date.date) {
    return new Date(a.date.date).getTime() > new Date(b.date.date).getTime();
  }

  return a.dateCreated.getTime() > b.dateCreated.getTime();
};

export const toReferenceDataView = (data: IReferenceData): ReferenceDataView => {
  const { date, base, symbol, rate, source } = data;
  const view: ReferenceDataStoreModel = {
    ...DefaultReferenceDataStoreModel,
    id: `${date.date}|${base}|${symbol}`,
    vid: null,
    dc: new Date().toISOString(),
    dm: new Date().toISOString(),
    rate,
    src: source,
  };
  return new ReferenceDataView(view);
};

export class ReferenceDataView extends ModelView implements IReferenceData {
  protected version: ReferenceDataModelVersion;

  date: GlossDateAsObjectType;
  base: string;
  id: ReferenceId;
  rate: number;
  symbol: string;
  source: ReferenceDataSource;

  constructor(model?: ReferenceDataStoreModel) {
    super(model);
    if (typeof model === "object") {
      const { date, base, symbol, rate } = this.castToReferenceDataView(model);
      this.date = date;
      this.base = base;
      this.symbol = symbol;
      this.rate = rate;
      this.source = model.src as ReferenceDataSource;
    } else {
      this.version = DefaultReferenceDataStoreModel.v;
      this.id = `${this.date}|${this.base}|${this.symbol}`;
    }
  }

  // todo @alex Need to set the right this.date toStringFunction and not use this.date.date
  toStoreModel(): ReferenceDataStoreModel {
    return {
      rate: this.rate,
      v: this.version,
      vid: this.vaultId,
      dc: this.dateCreated.toISOString(),
      dm: this.dateModified.toISOString(),
      id: `${this.date.date}|${this.base}|${this.symbol}`,
      src: this.source,
    };
  }

  clone(): ReferenceDataView {
    return new ReferenceDataView(this.toStoreModel());
  }

  private castToReferenceDataView(model: ReferenceDataStoreModel): IReferenceData {
    /** @Sinan add a validator for the id to fit in the structure*/

    const idParts = model.id.split("|") as ReferenceIdParts;
    return {
      date: {
        date: idParts[0],
        time: null,
        timeZone: null,
        reliableTimeSource: false,
      },
      base: idParts[1],
      symbol: idParts[2],
      rate: Number(model.rate),
      source: model.src as ReferenceDataSource,
    };
  }
}
